<template>
  <div class="home mt-2">
    <!-- Encabezado estandarizado con filtros -->
    <entity-header
      title="Usuarios"
      filter-id="filtroscollapse"
      :show-filters="true"
      :show-export="false"
      :show-new="true"
      new-label="Nuevo Usuario"
      new-tooltip="Nuevo Usuario"
      :has-active-filters="hasActiveFilters"
      @toggle-filters="cargarFiltros"
      @new="openCreateModal"
      @search="usuariosBuscar"
    >
      <template #filters>
        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="usuario_select">Por Usuario</label>
          <input
            type="text"
            class="form-control"
            id="usuario_select"
            placeholder="Filtrar por usuario..."
            @keyup.enter="usuariosBuscar"
            v-model="usuario_sel"
            aria-label="Usuario"
          />
        </div>

        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="integrador_select">Por Integrador</label>
          <select
            class="form-control"
            v-model="id_integrador_sel"
            name="atributo"
            aria-label="Atributos"
            id="integrador_select"
          >
            <option selected value="">Todos los integradores</option>
            <template v-if="integradoresList && integradoresList.length">
              <option
                v-for="integrador in integradoresList"
                :key="integrador.id_integrador"
                :value="integrador.id_integrador"
              >
                {{ integrador.integrador }}
              </option>
            </template>
          </select>
        </div>

        <div class="form-group col-md-2 mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="rol_select">Por Rol</label>
          <select
            class="form-control"
            v-model="rol_sel"
            name="atributo"
            aria-label="Tipo"
            id="rol_select"
          >
            <option selected value="">Todos los roles</option>
            <option
              v-for="r in roles"
              :key="r.id_rol"
              :value="r.nombre"
            >
              {{ r.descripcion || r.nombre }}
            </option>
          </select>
        </div>

        <div class="form-group col-md-2 mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="enabled_select">Por Estado</label>
          <select
            class="form-control"
            v-model="enabled_sel"
            name="atributo"
            aria-label="Activo"
            id="enabled_select"
          >
            <option selected value="">Todos los estados</option>
            <option value="0">Inactivo</option>
            <option value="1">Activo</option>
          </select>
        </div>
      </template>
    </entity-header>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">{{ error }}</div>

    <!-- Tabla estandarizada con DataTable -->
    <data-table
      v-if="usuariosBusqueda && usuariosBusqueda.data && usuariosBusqueda.data.length"
      :columns="columns"
      :data="usuariosBusqueda.data"
      id-key="id_usuario"
      :order-by-field="orderByField"
      :order-by-direction="orderByDirection"
      actions-width="150"
      @sort="orderByfield"
    >
      <!-- Slot celda Integrador (soporte múltiple) -->
      <template #cell-integrador="{ item }">
        <template v-if="item.integradores && item.integradores.length">
          <span
            v-for="(integ, idx) in item.integradores"
            :key="integ.id_integrador"
          >
            <span v-if="idx > 0">, </span>{{ integ.integrador }}
          </span>
        </template>
        <template v-else>
          {{ item.integrador }}
          <small v-if="item.id_integrador">(ID:{{ item.id_integrador }})</small>
        </template>
      </template>

      <!-- Slot celda Rol -->
      <template #cell-rol="{ item }">
        <span :class="item.rol == 'admin' ? 'text-danger font-weight-bold' : ''">
          {{ getRolNombre(item.rol) }}
        </span>
      </template>

      <!-- Slot Acciones -->
      <template #actions="{ item }">
        <table-actions
          :item="item"
          active-key="enabled"
          @edit="openEditModal(item)"
          @toggle="openToggleModal(item)"
        >
          <template #extra>
            <button
              class="btn btn-default btn-sm mr-1 pt-0 pb-1"
              data-toggle="tooltip"
              data-placement="top"
              title="Ver, Generar o Regenerar API Token y API Secret"
              @click="openTokensModal(item)"
            >
              <small class="bi-recycle"></small>
            </button>
            <button
              class="btn btn-info btn-sm mr-1 pt-0 pb-1"
              data-toggle="tooltip"
              data-placement="top"
              title="Cambiar Password"
              @click="openPasswordModal(item)"
            >
              <small class="bi-key-fill"></small>
            </button>
          </template>
        </table-actions>
      </template>
    </data-table>

    <!-- Paginador -->
    <Paginador
      v-if="usuariosBusqueda && usuariosBusqueda.pages"
      :pages="usuariosBusqueda.pages"
      @selPage="changePage"
    />

    <!-- Estado vacío -->
    <empty-state
      :show="Boolean(post && !(usuariosBusqueda && usuariosBusqueda.data && usuariosBusqueda.data.length))"
      message="No se obtuvieron usuarios con el criterio seleccionado."
    />

    <!-- Modal de Formulario (Crear / Editar) -->
    <usuario-form-modal
      :show="showUserModal"
      :operation="userOperation"
      :user="selectedUser"
      :roles="roles"
      :todos-integradores="todosIntegradores"
      :permisos="permisos"
      :rol-permisos="rolPermisos"
      @saved="onUserSaved"
      @close="showUserModal = false"
    />

    <!-- Modal de Cambio de Contraseña -->
    <usuario-password-modal
      :show="showPasswordModal"
      :user="selectedUser"
      @saved="getUsuariosBusqueda(false)"
      @close="showPasswordModal = false"
    />

    <!-- Modal de Tokens API -->
    <usuario-tokens-modal
      :show="showTokensModal"
      :user="selectedUser"
      @tokens-generated="onTokensGenerated"
      @close="showTokensModal = false"
    />

    <!-- Modal de Suspender / Reactivar (Toggle Activo) -->
    <crud-modal
      :show="showToggleModal"
      :title="selectedUser && selectedUser.enabled == '1' ? 'Suspender Usuario' : 'Activar Usuario'"
      size="md"
      :is-toggle="true"
      :toggle-message="selectedUser && selectedUser.enabled == '1' ? 'Al suspender este usuario perderá acceso a la plataforma.' : ''"
      :save-text="selectedUser && selectedUser.enabled == '1' ? 'Suspender' : 'Activar'"
      :save-variant="selectedUser && selectedUser.enabled == '1' ? 'warning' : 'success'"
      :loading="toggleLoading"
      @save="confirmToggleUsuario"
      @close="showToggleModal = false"
    >
      <div v-if="selectedUser" class="small">
        <div class="form-group mb-2">
          <label class="font-weight-bold text-muted mb-0">ID Usuario:</label>
          <div>{{ selectedUser.id_usuario }}</div>
        </div>
        <div class="form-group mb-2">
          <label class="font-weight-bold text-muted mb-0">Usuario:</label>
          <div><strong>{{ selectedUser.name }}</strong></div>
        </div>
        <div class="form-group mb-0">
          <label class="font-weight-bold text-muted mb-0">Email:</label>
          <div>{{ selectedUser.email || '-' }}</div>
        </div>
      </div>
    </crud-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { tableSortMixin } from "@/mixins/tableSortMixin";
import EntityHeader from "./utiles/EntityHeader.vue";
import DataTable from "./utiles/DataTable.vue";
import TableActions from "./utiles/TableActions.vue";
import EmptyState from "./utiles/EmptyState.vue";
import Paginador from "./Paginador.vue";
import UsuarioFormModal from "./usuarios/UsuarioFormModal.vue";
import UsuarioPasswordModal from "./usuarios/UsuarioPasswordModal.vue";
import UsuarioTokensModal from "./usuarios/UsuarioTokensModal.vue";
import CrudModal from "./utiles/CrudModal.vue";

export default {
  name: "Usuarios",
  mixins: [tableSortMixin],

  components: {
    EntityHeader,
    DataTable,
    TableActions,
    EmptyState,
    Paginador,
    UsuarioFormModal,
    UsuarioPasswordModal,
    UsuarioTokensModal,
    CrudModal,
  },

  data() {
    return {
      success: null,
      error: null,
      enabled_sel: "",
      rol_sel: "",
      id_integrador_sel: "",
      usuario_sel: null,
      usuariosBusqueda: null,
      post: false,
      orderByField: "name",
      page: 1,
      errors: [],

      // Catálogos para filtros y formularios
      roles: [],
      permisos: [],
      rolPermisos: [],
      todosIntegradores: [],

      // Modales y datos de selección
      selectedUser: null,
      showUserModal: false,
      userOperation: "add",
      showPasswordModal: false,
      showTokensModal: false,
      showToggleModal: false,
      toggleLoading: false,

      // Columnas para DataTable
      columns: [
        { label: "Id", field: "id_usuario", width: "80", align: "right", sortable: true },
        { label: "Usuario", field: "name", align: "left", sortable: true },
        { label: "Email", field: "email", align: "left", sortable: true },
        { label: "Integrador", field: "integrador", align: "left", sortable: true },
        { label: "Rol", field: "rol", align: "left", sortable: true },
        { label: "Estado", field: "enabled", type: "badge", width: "110", align: "center", sortable: true },
      ],
    };
  },

  created() {
    this.fetchRoles();
    this.fetchPermisos();
    if (this.isAdmin()) {
      this.fetchIntegradores();
      this.fetchTodosIntegradores();
    }
    this.getUsuariosBusqueda();
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list.sort((a, b) =>
        String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
      );
    },

    hasActiveFilters() {
      return (
        Boolean(this.usuario_sel) ||
        Boolean(this.id_integrador_sel) ||
        Boolean(this.rol_sel) ||
        (this.enabled_sel !== null && this.enabled_sel !== undefined && this.enabled_sel !== "")
      );
    },
  },

  methods: {
    ...mapActions("integrador", ["fetchIntegradores"]),

    isAdmin() {
      return this.user && this.user.rol == "admin";
    },

    cargarFiltros() {
      this.fetchRoles();
      this.fetchPermisos();
      this.fetchTodosIntegradores();
      if (this.isAdmin()) {
        this.fetchIntegradores();
      }
      return Promise.resolve(null);
    },

    fetchTodosIntegradores() {
      return axios
        .post(process.env.VUE_APP_API_URL + "integrador/all")
        .then((response) => {
          if (response && response.data && Array.isArray(response.data.data)) {
            const list = [...response.data.data];
            list.sort((a, b) =>
              String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
            );
            this.todosIntegradores = list;
          }
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },

    fetchRoles() {
      return axios
        .post(process.env.VUE_APP_API_URL + "usuario/roles")
        .then((response) => {
          if (response && response.data && response.data.data) {
            this.roles = response.data.data;
          }
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },

    fetchPermisos() {
      return axios
        .post(process.env.VUE_APP_API_URL + "usuario/permisos")
        .then((response) => {
          if (response && response.data) {
            this.permisos = response.data.data || [];
            this.rolPermisos = response.data.rol_permisos || [];
          }
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },

    getRolNombre(rolVal) {
      if (!rolVal) return "-";
      if (this.roles && this.roles.length) {
        const found = this.roles.find(
          (r) => r.nombre === rolVal || String(r.id_rol) === String(rolVal)
        );
        if (found) {
          return found.descripcion || found.nombre;
        }
      }
      if (rolVal === "admin") return "Administrador";
      if (rolVal === "integrador") return "Integrador";
      return rolVal;
    },

    usuariosBuscar() {
      this.changePage(1);
    },

    changePage(page) {
      this.page = page;
      this.getUsuariosBusqueda(false);
    },

    orderByfield(field) {
      this.toggleOrder(field);
      this.getUsuariosBusqueda(false);
    },

    getUsuariosBusqueda(hideResult = true) {
      if (hideResult) this.usuariosBusqueda = null;
      this.post = false;
      this.$store.commit("setLoading", true);
      return axios
        .post(process.env.VUE_APP_API_URL + "usuario/busqueda", {
          page: this.page,
          id_integrador: this.id_integrador_sel,
          rol: this.rol_sel,
          name: this.usuario_sel,
          enabled: this.enabled_sel,
          order_by: [{ name: this.orderByField, order: this.orderByDirection }],
        })
        .then((response) => {
          response.data.data.forEach((item) => {
            item.showSecret = true;
            item.showApiToken = false;
          });
          this.usuariosBusqueda = response.data;
        })
        .catch((e) => {
          this.errors.push(e);
          if (e.response && e.response.status == 429) {
            Swal.fire({
              title: "Error!",
              text: "Espere un momento y vuelva a intentarlo.",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al buscar los usuarios",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          this.post = true;
        });
    },

    openCreateModal() {
      this.cargarFiltros();
      this.selectedUser = { enabled: 1, rol: "publica" };
      this.userOperation = "add";
      this.showUserModal = true;
    },

    openEditModal(item) {
      this.cargarFiltros();
      this.selectedUser = Object.assign({}, item);
      this.userOperation = "edit";
      this.showUserModal = true;
    },

    onUserSaved() {
      this.showUserModal = false;
      this.getUsuariosBusqueda();
    },

    openPasswordModal(item) {
      this.selectedUser = Object.assign({}, item);
      this.showPasswordModal = true;
    },

    openTokensModal(item) {
      const hasIntegrador =
        (item.integradores && item.integradores.length > 0) || item.id_integrador;
      if (!hasIntegrador) {
        Swal.fire({
          title: "Error!",
          text: "Debe asignar al menos un integrador al usuario para generar los códigos para acceder a las APIs",
          icon: "error",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      this.selectedUser = Object.assign({}, item);
      this.showTokensModal = true;
    },

    onTokensGenerated({ userId, apitoken, apisecret }) {
      if (this.selectedUser && this.selectedUser.id_usuario === userId) {
        this.$set(this.selectedUser, "apitoken", apitoken);
        this.$set(this.selectedUser, "api_token", apitoken);
        this.$set(this.selectedUser, "apisecret", apisecret);
        this.$set(this.selectedUser, "api_secret", apisecret);
      }
      if (this.usuariosBusqueda && Array.isArray(this.usuariosBusqueda.data)) {
        const item = this.usuariosBusqueda.data.find(
          (u) => u.id_usuario === userId
        );
        if (item) {
          if (apitoken) {
            item.apitoken = apitoken;
            item.api_token = apitoken;
          }
          if (apisecret) {
            item.apisecret = apisecret;
            item.api_secret = apisecret;
          }
        }
      }
    },

    openToggleModal(item) {
      this.selectedUser = Object.assign({}, item);
      this.showToggleModal = true;
    },

    confirmToggleUsuario() {
      if (!this.selectedUser) return;
      this.toggleLoading = true;
      axios
        .post(process.env.VUE_APP_API_URL + "usuario/delete", {
          id_usuario: this.selectedUser.id_usuario,
          enabled: this.selectedUser.enabled == "1" ? "0" : "1",
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            Swal.fire({
              title: "Error!",
              text: response.data.error || "Error al actualizar estado del usuario",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            const wasEnabled = this.selectedUser.enabled == "1";
            this.showToggleModal = false;
            this.selectedUser = null;
            Swal.fire({
              title: "¡Éxito!",
              text: wasEnabled
                ? "Usuario suspendido correctamente"
                : "Usuario activado correctamente",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            this.getUsuariosBusqueda();
          }
        })
        .catch((e) => {
          Swal.fire({
            title: "Error!",
            text:
              (e.response && e.response.data && e.response.data.error) ||
              "Ha ocurrido un error al actualizar el usuario",
            icon: "error",
            confirmButtonText: "Aceptar",
          });
        })
        .finally(() => {
          this.toggleLoading = false;
        });
    },
  },
};
</script>
